import { useEffect, useState } from "react";

import styles from "./StepRegisterPoliceman.module.scss";

import { Tab } from "../../../TabHeader";
import { usePolicemanRegister } from "contexts/register/PolicemanContext";
import {
  handlePolicemanRegisterSubmit,
  handlePolicemanUpdateSubmit,
} from "services/handles/PolicemanHandles";

import { AddressForm } from "../../../Forms/Policeman/AddressForm";
import { PolicemanForm } from "../../../Forms/Policeman/PolicemanForm";
import { BankingForm } from "../../../Forms/Policeman/BankingForm";

import {
  IGetByIdPoliceman,
  IPoliceman,
  PolicemanPicturesParam,
} from "interfaces/Policeman";
import { VehicleForm } from "../../../Forms/Policeman/VehicleForm";
import { StepModalTemplate } from "templates/StepModalTemplate";
import { IPolicemanRegister } from "contexts/register/IPolicemanContext";
import { Users } from "pages/Navigation/Users";
import { IExpressValidatorError } from "interfaces/ExpressError";
import { transformValidatorError } from "utils/transformValidatorError";
import { PolicemanValuesForm } from "components/Forms/Policeman/ValuesForm";
import DocumentationForm from "components/Forms/Policeman/DocumentationForm";

type Props = {
  opened: boolean;
  setOpened: (value: boolean) => void;
  policemanData?: IPoliceman;
  moreInfoPolicemen?: IGetByIdPoliceman;
};

type ExpressValidatorErrors = {
  msg: string;
  param: string;
  location: string;
};

const tabs: Tab[] = [
  { index: 0, text: "Dados pessoais" },
  { index: 1, text: "Dados bancários" },
  { index: 2, text: "Endereço" },
  { index: 3, text: "Veículo" },
  { index: 4, text: "Valores para pagamento" },
  { index: 5, text: "Documentação" },
];

function StepRegisterPoliceman({
  opened,
  setOpened,
  policemanData,
  moreInfoPolicemen,
}: Props) {
  const {
    policeman,
    contact,
    vehicle,
    address,
    files,
    bankingData,
    setErrors,
    errors,
    retrievePoliceman,
    resetPoliceman,
    searching,
    selectedProvider,
  } = usePolicemanRegister();

  const [tab, setTab] = useState<Tab>(tabs[0]);

  const [invalidTabs, setInvalidTabs] = useState<Tab[]>([]);

  const [sending, setSending] = useState<boolean>(false);

  useEffect(() => {
    if (policemanData) {
      setInvalidTabs([]);
      setErrors({});
      retrievePoliceman(policemanData.user.id);
      return;
    }

    resetPoliceman();
  }, [policemanData]); // eslint-disable-line react-hooks/exhaustive-deps

  function setTabAsInvalid(tab: Tab) {
    setInvalidTabs((value) => [...value, tab]);
  }

  function treatValidatorErrors(validatorErrors: IExpressValidatorError) {
    setInvalidTabs([]);

    if (validatorErrors === undefined) return;

    const { errors, invalidFormTypes } =
      transformValidatorError(validatorErrors);

    for (const type of invalidFormTypes) {
      if (type.includes("user") || type.includes("policemen"))
        setTabAsInvalid(tabs[0]);
      if (type.includes("provider")) setTabAsInvalid(tabs[0]);
      if (type.includes("bankingData")) setTabAsInvalid(tabs[1]);
      if (type.includes("address")) setTabAsInvalid(tabs[2]);
      if (type.includes("policeman")) setTabAsInvalid(tabs[4]);
    }

    setErrors(errors);
  }

  function treatDuplicatedValidator(data: {
    [key: string]: { message: string; value: any };
  }) {
    let response: any = {};
    for (const key in data) {
      if (key.includes("cnpj") || key.includes("cpf") || key.includes("rg"))
        setTabAsInvalid(tabs[0]);
      response[key] = data[key].message;
    }
    setErrors((err) => {
      return { ...err, ...response };
    });
  }

  useEffect(() => {
    if (!Object.keys(errors).some((errorKey) => errorKey.includes("user"))) {
      setInvalidTabs((current) => current.filter((tab) => tab.index !== 0));
    }
    if (
      !Object.keys(errors).some((errorKey) => errorKey.includes("bankingData"))
    ) {
      setInvalidTabs((current) => current.filter((tab) => tab.index !== 1));
    }
    if (!Object.keys(errors).some((errorKey) => errorKey.includes("address"))) {
      setInvalidTabs((current) => current.filter((tab) => tab.index !== 2));
    }
    if (
      !Object.keys(errors).some((errorKey) =>
        errorKey.includes("policeman.vehicleValues")
      )
    ) {
      setInvalidTabs((current) => current.filter((tab) => tab.index !== 4));
    }
  }, [errors]);
  function validatePersonData(policeman: IPolicemanRegister) {
    const defaultValues = {
      cpf: "xxx.xxx.xxx-xx",
      cnpj: "xx.xxx.xxx/xxxx-xx",
      rg: "xx.xxx.xxx-x",
    };

    if (policeman.user) {
      policeman?.user?.cnpj === defaultValues.cnpj &&
        (policeman.user.cnpj = undefined);
      policeman?.user?.cpf === defaultValues.cpf && (policeman.user.cpf = "");
      policeman?.user?.rg === defaultValues.rg && (policeman.user.rg = "");

      if (!policeman?.user?.cnpj) {
        policeman.user.cnpj = defaultValues.cnpj;
        return;
      }

      if (!policeman?.user?.cpf && !policeman?.user?.rg) {
        policeman.user.cpf = defaultValues.cpf;
        policeman.user.rg = defaultValues.rg;
        return;
      }
    }
  }

  async function handleSubmit() {
    setSending(true);

    const _policeman = { ...policeman };
    const {
      user,
      occurrencesDenied,
      occurrencesAttended,
      approved,
      ...policemanWithoutUser
    } = _policeman;

    validatePersonData(policemanWithoutUser);

    if (!address.complement) delete address?.complement;
    if (!address.reference) delete address?.reference;
    if (!vehicle.description) delete vehicle?.description;
    if (!bankingData?.accountData?.pix) delete bankingData?.accountData?.pix;

    _policeman.user && (_policeman.user["cnpj"] = policemanWithoutUser.cnpj);

    if (
      policemanData &&
      contact &&
      _policeman.user &&
      vehicle &&
      address &&
      bankingData
    ) {
      delete _policeman?.user.address;
      delete _policeman?.user.bankAccount;
      delete _policeman?.user.contact;
      delete _policeman?.user.policeman;
      delete _policeman?.user.vehicle;

      // removing the keys without pictures files to send to database only the updated pictures
      const notNullPolicemanPictures = Object.entries(files).reduce(
        (acc, [key, file]) => {
          if (!file) return acc;

          return { ...acc, [key]: file };
        },
        {}
      );

      return await handlePolicemanUpdateSubmit({
        contact: { ...contact, id: moreInfoPolicemen?.contact?.id },
        policeman: { ...policemanWithoutUser, id: policemanData.id },
        user: { ..._policeman?.user, id: policemanData.user.id },
        address: { ...address, id: moreInfoPolicemen?.address.id },
        baking: { ...bankingData, id: moreInfoPolicemen?.bankAccount.id },
        vehicle: { ...vehicle, id: moreInfoPolicemen?.vehicle.id },
        pictures: notNullPolicemanPictures,
      })
        .then((res) => {
          if (res.status === true) {
            localStorage.setItem(
              "message",
              "Os dados do Pronta-resposta foram salvos corretamente."
            );
            window.location.reload();
          }
          if (res.data.duplicated) {
            treatDuplicatedValidator(res.data.error);
          } else {
            treatValidatorErrors(res.data.error);
          }
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          setSending(false);
        });
    }

    if (
      policeman &&
      contact &&
      _policeman.user &&
      vehicle &&
      address &&
      bankingData
    ) {
      setInvalidTabs([]);

      return await handlePolicemanRegisterSubmit({
        policeman: _policeman,
        contact: contact,
        vehicle: vehicle,
        address: address,
        banking: bankingData,
        user: _policeman.user,
        provider: selectedProvider,
        pictures: files as PolicemanPicturesParam,
      })
        .then((res) => {
          if (res.status === true) {
            localStorage.setItem(
              "message",
              "Os dados do Pronta-resposta foram cadastrados corretamente."
            );
            window.location.reload();
          }
          if (res.data.duplicated) {
            treatDuplicatedValidator(res.data.error);
          } else {
            treatValidatorErrors(res.data.error);
          }
        })
        .catch((error) => {
          treatValidatorErrors(error.data);
        })
        .finally(() => {
          setSending(false);
        });
    }

    setSending(false);
  }

  function onTabClick(_tab: Tab) {
    setTab(_tab);
  }

  return (
    <StepModalTemplate
      modalClassname={styles.modalContent}
      deactivatedTabs={[]}
      opened={opened}
      disableButton={Object.keys(errors).length > 0}
      setOpened={setOpened}
      buttonText={policemanData ? "Salvar edição" : "Efetuar cadastro"}
      handleSubmit={handleSubmit}
      tabs={tabs}
      setSelectedTab={onTabClick}
      selectedTab={tab}
      sending={sending}
      title={`${policemanData ? "Editar" : "Cadastrar"} pronta-resposta`}
      invalidTabs={invalidTabs}
    >
      <div className={styles.container}>
        {searching && (
          <div className={styles.loadingWrapper}>
            <span>Buscando dados do pronta-resposta...</span>
          </div>
        )}

        {!searching && (
          <div>
            {tab.index === 0 && (
              <PolicemanForm
                isEditting={policemanData !== undefined}
                contactData={moreInfoPolicemen?.contact}
                policemanData={policemanData}
              />
            )}

            {tab.index === 1 && (
              <BankingForm updateBankingData={moreInfoPolicemen?.bankAccount} />
            )}

            {tab.index === 2 && (
              <AddressForm updateAddressData={moreInfoPolicemen?.address} />
            )}

            {tab.index === 3 && (
              <VehicleForm updateVehicleData={moreInfoPolicemen?.vehicle} />
            )}

            {tab.index === 4 && (
              <PolicemanValuesForm
                updateValuesData={{
                  patrimonialValues:
                    moreInfoPolicemen?.policeman?.patrimonialValues,
                  vehicleValues: moreInfoPolicemen?.policeman?.vehicleValues,
                }}
              />
            )}

            {tab.index === 5 && (
              <DocumentationForm
                defaultPicturesObject={moreInfoPolicemen?.policeman?.pictures}
              />
            )}
          </div>
        )}
      </div>
    </StepModalTemplate>
  );
}

export { StepRegisterPoliceman };
